import { Box, Step, StepConnector, StepLabel, Stepper, Typography } from "@mui/material";
import { getUtcDisplayDate } from "encamp-shared/src/utils/date";

import { SectionHeader } from "./SectionHeader";
import { TimelineEvent } from "./summaryStatus";

/** Filled dot for events that have happened, hollow outline for pending ones.
 * Compliance-path events ("warning" tone) fill orange instead of green. */
function FlowDot({ completed, tone }: { completed?: boolean; tone?: "default" | "warning" }) {
  const doneColor = tone === "warning" ? "warning.main" : "success.main";
  return (
    <Box
      sx={{
        width: 14,
        height: 14,
        borderRadius: "50%",
        boxSizing: "border-box",
        bgcolor: completed ? doneColor : "background.paper",
        border: 2,
        borderColor: completed ? doneColor : "action.disabled",
      }}
    />
  );
}

interface TimelineSectionProps {
  events: TimelineEvent[];
}

/**
 * The manifest lifecycle as a left-to-right flow: each stage is a node on a
 * connected line, labeled with what happened and when. Events are already
 * ordered chronologically (see buildTimelineEvents); pending stages trail with
 * a hollow node and an em dash. Source-agnostic.
 */
export function TimelineSection({ events }: TimelineSectionProps) {
  if (events.length === 0) return null;

  return (
    <Box>
      <SectionHeader title="Timeline" />
      <Stepper
        alternativeLabel
        activeStep={events.filter((e) => e.done).length}
        connector={<StepConnector />}
      >
        {events.map((event) => (
          <Step key={event.key} completed={event.done}>
            <StepLabel
              StepIconComponent={() => <FlowDot completed={event.done} tone={event.tone} />}
              optional={
                <Typography
                  variant="caption"
                  color={event.done ? "text.secondary" : "text.disabled"}
                  sx={{ display: "block", textAlign: "center" }}
                >
                  {event.date ? getUtcDisplayDate(event.date) : "—"}
                </Typography>
              }
            >
              <Typography variant="body2" color={event.done ? "text.primary" : "text.disabled"}>
                {event.label}
              </Typography>
            </StepLabel>
          </Step>
        ))}
      </Stepper>
    </Box>
  );
}
